import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_VIDEO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_my-menu-app-2/artifacts/skmcrx63_Post%20SPA%20E%20Cena%20al%20Ristorante.mp4";

/**
 * Filtra i video in base alla fascia oraria configurata. Un video senza
 * schedule (o senza from/to) è sempre attivo. Se ``from > to`` la fascia
 * è considerata attraversare la mezzanotte (es. 20:00 → 02:00).
 */
function filterByTime(videos) {
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  const parse = (s) => {
    if (!s || typeof s !== "string") return null;
    const [h, m] = s.split(":").map((x) => parseInt(x, 10));
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };
  const enabled = videos.filter((v) => {
    const sch = v.schedule;
    if (!sch || !sch.enabled) return true; // no schedule = sempre attivo
    const from = parse(sch.from);
    const to = parse(sch.to);
    if (from == null || to == null) return true;
    if (from === to) return true;
    if (from < to) return mins >= from && mins < to;
    // Traverse midnight (es. 20:00 → 02:00)
    return mins >= from || mins < to;
  });
  // Se nessun video è attivo in questa fascia, ritorna comunque tutti
  // (fallback per evitare schermo nero)
  return enabled.length > 0 ? enabled : videos;
}


/**
 * Fullscreen looping video for the Fire TV Stick.
 * Route: /tv-loop  or  /tv-loop?src=<url>&muted=0
 *
 * Silk Browser on Fire TV blocks autoplay until first user interaction —
 * we show a "Tap OK to start" overlay that becomes the required tap.
 */
export default function TvLoopPage() {
  const [videoList, setVideoList] = useState([]); // playlist di URL
  const [idx, setIdx] = useState(0); // indice corrente nella playlist
  const [muted, setMuted] = useState(true);
  const [status, setStatus] = useState("loading"); // loading | ready | error | needs-tap
  const [errMsg, setErrMsg] = useState("");
  const videoRef = useRef(null);

  const videoUrl = videoList[idx] || "";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("src");
    if (params.get("muted") === "0") setMuted(false);
    if (override) {
      setVideoList([override]);
      return;
    }
    axios
      .get(`${API}/tv-loop/config?_t=${Date.now()}`)
      .then((r) => {
        const abs = (u) => u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
        const videos = filterByTime(r.data?.videos || []).map((v) => abs(v.url)).filter(Boolean);
        if (videos.length > 0) {
          setVideoList(videos);
        } else if (r.data?.video_url) {
          setVideoList([abs(r.data.video_url)]);
        } else {
          setVideoList([DEFAULT_VIDEO]);
        }
      })
      .catch((e) => {
        setErrMsg(`Config API: ${e.message}`);
        setVideoList([DEFAULT_VIDEO]);
      });
  }, []);

  // Ricarica la config ogni 15s in modo che nuovi video caricati dall'admin
  // finiscano automaticamente nella playlist senza dover ricaricare la pagina.
  useEffect(() => {
    const iv = setInterval(() => {
      // cache-bust per evitare che Silk Browser (Fire TV) serva risposte
      // in cache invece di leggere la lista aggiornata dal server
      axios.get(`${API}/tv-loop/config?_t=${Date.now()}`).then((r) => {
        const abs = (u) => u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
        const videos = filterByTime(r.data?.videos || []).map((v) => abs(v.url)).filter(Boolean);
        if (videos.length > 0) {
          setVideoList((prev) => {
            const same = prev.length === videos.length && prev.every((u, i) => u === videos[i]);
            return same ? prev : videos;
          });
          // se idx è ora fuori range, resetta a 0
          setIdx((i) => (i >= videos.length ? 0 : i));
        }
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.overflow = "hidden";
    document.body.style.background = "#000";
  }, []);

  // ---- KEEP ALIVE (evita screensaver Fire TV) --------------------
  useEffect(() => {
    let wakeLock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && !cancelled) {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener?.("release", () => {
            // Se il sistema rilascia il lock (es. tab in background), ri-richiediamo
            if (!cancelled) setTimeout(acquire, 2000);
          });
        }
      } catch (_e) { /* not supported or denied — nessun problema */ }
    };
    acquire();
    // Ri-acquisisce il wake lock ogni volta che la pagina torna visibile
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    // Nudge di attività ogni 20s: mousemove sintetico + tocco DOM.
    // Serve a segnalare a Silk Browser (Fire TV) che l'utente è "attivo"
    // e ritarda il salvaschermo del sistema.
    const iv = setInterval(() => {
      try {
        document.dispatchEvent(new MouseEvent("mousemove", {
          clientX: Math.floor(Math.random() * 100),
          clientY: Math.floor(Math.random() * 100),
          bubbles: true,
        }));
        // Anche un piccolo scroll per aggiornare l'attività
        window.scrollTo(0, window.scrollY + 0.1);
        window.scrollTo(0, 0);
      } catch (_e) {}
    }, 20000);

    // Silent Web-Audio ping: crea un'audio track a volume 0. Fire TV OS
    // considera le app che riproducono audio come "in uso" e le uccide
    // molto più tardi (o mai) rispetto a un'app "silenziosa".
    let audioCtx = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        audioCtx = new AC();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        gain.gain.value = 0.00001; // effettivamente muto
        osc.frequency.value = 20;   // sotto la soglia di udibilità
        osc.connect(gain).connect(audioCtx.destination);
        osc.start();
        // Se il contesto è sospeso (autoplay policy), prova a riprenderlo al primo tap
        const resumeCtx = () => { audioCtx?.resume?.(); };
        document.addEventListener("click", resumeCtx, { once: false });
        document.addEventListener("touchstart", resumeCtx, { once: false });
        document.addEventListener("keydown", resumeCtx, { once: false });
      }
    } catch (_e) {}

    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      try { wakeLock?.release(); } catch (_e) {}
      try { audioCtx?.close(); } catch (_e) {}
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    v.muted = muted;
    // Force reload del <video> — su Silk Browser Fire TV cambiare solo il
    // src non basta, il video precedente resta bufferizzato.
    try { v.load(); } catch (_e) {}
    let cancelled = false;
    const tryPlay = async (attempt = 0) => {
      try {
        await v.play();
        if (!cancelled) setStatus("ready");
      } catch (_e) {
        // Even if programmatic play() failed, the native autoplay muted may
        // have succeeded — check the paused flag before showing the overlay.
        if (!cancelled) {
          if (!v.paused) {
            setStatus("ready");
          } else if (attempt < 3) {
            // Silk sometimes needs a moment after the doc becomes visible.
            setTimeout(() => tryPlay(attempt + 1), 800);
          } else {
            setStatus("needs-tap");
          }
        }
      }
    };
    tryPlay();
    // Some browsers (Silk) fire playing/canplay slightly after our first attempt
    const onPlaying = () => { if (!cancelled) setStatus("ready"); };
    v.addEventListener("playing", onPlaying);
    return () => {
      cancelled = true;
      v.removeEventListener("playing", onPlaying);
    };
  }, [muted, videoUrl]);

  const startPlayback = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = muted;
      await v.play();
      setStatus("ready");
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } catch (e) {
      setErrMsg(`Play error: ${e.message}`);
      setStatus("error");
    }
  };

  return (
    <div
      data-testid="tv-loop-page"
      onClick={startPlayback}
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        loop={videoList.length <= 1}
        playsInline
        muted={muted}
        onEnded={() => {
          // In playlist mode (2+ video) avanza automaticamente al prossimo
          if (videoList.length > 1) {
            setIdx((i) => (i + 1) % videoList.length);
          }
        }}
        onCanPlay={() => setStatus((s) => (s === "loading" ? "ready" : s))}
        onError={(e) => {
          const code = e.currentTarget?.error?.code;
          const map = {
            1: "MEDIA_ERR_ABORTED",
            2: "MEDIA_ERR_NETWORK",
            3: "MEDIA_ERR_DECODE",
            4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
          };
          setErrMsg(`Video error: ${map[code] || code}`);
          setStatus("error");
        }}
        style={{
          width: "100vw",
          height: "100vh",
          objectFit: "cover",
        }}
      />

      {status === "needs-tap" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, #4a1c17 0%, #2b0f0b 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            padding: 40,
            textAlign: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "clamp(28px, 6vw, 64px)",
              fontWeight: 800,
              marginBottom: 8,
              color: "#f7ebdf",
              letterSpacing: "0.02em",
            }}
          >
            Il Pozzo dei Giganti
          </div>
          <div
            style={{
              fontSize: "clamp(14px, 2vw, 22px)",
              opacity: 0.75,
              marginBottom: 60,
              color: "#f7ebdf",
            }}
          >
            Post SPA · Cena al Ristorante
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); startPlayback(); }}
            autoFocus
            style={{
              padding: "24px 80px",
              background: "#f7ebdf",
              color: "#4a1c17",
              borderRadius: 16,
              fontSize: "clamp(22px, 4vw, 40px)",
              fontWeight: 700,
              border: "4px solid #f7ebdf",
              outline: "none",
              cursor: "pointer",
              boxShadow: "0 0 0 6px rgba(245,239,230,0.25)",
              animation: "tvPulse 1.6s ease-in-out infinite",
            }}
          >
            ▶ &nbsp; Premi &nbsp;OK
          </button>
          <div
            style={{
              marginTop: 40,
              fontSize: "clamp(11px, 1.4vw, 16px)",
              opacity: 0.5,
              maxWidth: 600,
            }}
          >
            Silk Browser richiede una conferma manuale per avviare i video.
            Per l'autoplay completo consigliamo VLC for Fire TV (vedi QR nel
            pannello admin).
          </div>
          <style>{`
            @keyframes tvPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 0 6px rgba(245,239,230,0.25); }
              50% { transform: scale(1.04); box-shadow: 0 0 0 12px rgba(245,239,230,0.15); }
            }
          `}</style>
        </div>
      )}

      {status === "error" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            padding: 40,
            textAlign: "center",
            fontFamily: "monospace",
          }}
        >
          <div style={{ fontSize: 20, marginBottom: 12, color: "#ff8a80" }}>
            ⚠ Errore video
          </div>
          <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 20, maxWidth: 800, wordBreak: "break-all" }}>
            {errMsg || "unknown"}
          </div>
          <div style={{ fontSize: 12, opacity: 0.6, maxWidth: 800, wordBreak: "break-all" }}>
            URL: {videoUrl}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.location.reload();
            }}
            style={{
              marginTop: 24,
              padding: "12px 32px",
              background: "#4a1c17",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            Ricarica
          </button>
        </div>
      )}

      {/* mute/unmute pill */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMuted((m) => !m);
        }}
        data-testid="tv-loop-mute"
        style={{
          position: "absolute",
          bottom: 20,
          right: 20,
          padding: "8px 14px",
          borderRadius: 999,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.3)",
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        {muted ? "Audio OFF" : "Audio ON"}
      </button>

      {/* Status indicator top-left (only during loading) */}
      {status === "loading" && (
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            color: "#f7ebdf",
            fontSize: 14,
            fontFamily: "monospace",
          }}
        >
          Caricamento…
        </div>
      )}
    </div>
  );
}
